import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout, useOrderAlerts } from "../components/tabon/AppLayout";
import { NewOrderModal } from "../components/tabon/Orders";
import { useAuth } from "../contexts/AuthContext";
import { api, formatError } from "../lib/api";
import { printRoundKOT } from "../lib/kot";
import { printInvoice } from "../lib/print";
import { toast } from "sonner";
import { ChevronLeft, Plus, Printer, CreditCard, Users, Clock, GitMerge, XCircle, LayoutGrid, MoreVertical, Minus, X, Receipt } from "lucide-react";

const minsSince = (iso) => {
  if (!iso) return "";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

// The five tile states (G-7): colour AND a word. There is NO "paid awaiting clearing" state —
// a settled check closes and the table is free again. Precedence: bill printed > food ready >
// in kitchen > running > free (a ready round takes the tile even if another is still cooking).
const TILE_STATE = {
  free:    { label: "Free",         wordCls: "text-[#666]",      tileCls: "border-white/10 bg-[#0F0F0F] hover:border-white/25" },
  running: { label: "Running",      wordCls: "text-[#D78CFF]",   tileCls: "border-[#9D00FF]/50 bg-[#1A1A1A]" },
  kitchen: { label: "In kitchen",   wordCls: "text-amber-300",   tileCls: "border-amber-500/50 bg-amber-500/[0.07]" },
  ready:   { label: "Food ready",   wordCls: "text-emerald-300", tileCls: "border-emerald-500/60 bg-emerald-500/10" },
  printed: { label: "Bill printed", wordCls: "text-sky-300",     tileCls: "border-sky-500/50 bg-sky-500/[0.07]" },
};
function tileStateKey(chk) {
  if (!chk) return "free";
  if (chk.status === "printed") return "printed";
  const ks = (chk.rounds || []).filter((r) => !r.cancelled).map((r) => r.kitchen_status || "new");
  if (ks.includes("ready")) return "ready";
  if (ks.some((s) => s === "new" || s === "preparing")) return "kitchen";
  return "running";   // open, nothing live in the kitchen (e.g. just seated, or all rounds served)
}

// The money rows ABOVE charges, in order: Subtotal, Discount, Items made free, Loyalty, Net,
// CGST, SGST. Charges (service charge etc.), Round off and Total are rendered separately in the
// view so the service charge can carry an add/remove control. Every figure comes from the API's
// `bill` object; the client never re-rounds (P0-3). Comps are split out of the bill's aggregate
// discount_total — using the comp adjustments' stored amounts — so they read as "Items made free"
// (matching the struck-through FREE lines) rather than a mystery discount.
function billRows(bill, adjustments) {
  const b = bill || {};
  const gstRate = Number(b.gst_rate) || 0;
  const gst = Number(b.gst_amount) || 0;
  const taxLines = Array.isArray(b.tax_lines) ? b.tax_lines : [];
  let cgst = 0, sgst = 0;
  if (taxLines.length) {
    taxLines.forEach((t) => { cgst += Number(t.cgst) || 0; sgst += Number(t.sgst) || 0; });
  } else {
    cgst = Math.round((gst / 2) * 100) / 100;
    sgst = Math.round((gst - cgst) * 100) / 100;
  }
  const compTotal = (adjustments || []).filter((a) => a.type === "comp")
    .reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const discountOnly = Math.round(((Number(b.discount_total) || 0) - compTotal) * 100) / 100;
  const rows = [{ label: "Subtotal", amt: Number(b.subtotal) || 0 }];
  if (discountOnly > 0) rows.push({ label: "Discount", amt: -discountOnly, neg: true });
  if (compTotal > 0) rows.push({ label: "Items made free", amt: -compTotal, neg: true });
  if (Number(b.loyalty_redemption)) rows.push({ label: "Loyalty redemption", amt: -Number(b.loyalty_redemption), neg: true });
  rows.push({ label: "Net amount", amt: Number(b.net_amount) || 0 });
  if (gst > 0 && gstRate > 0) {
    rows.push({ label: `CGST @${gstRate / 2}%`, amt: cgst });
    rows.push({ label: `SGST @${gstRate / 2}%`, amt: sgst });
  }
  return rows;
}

export default function Tables() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // checkRev ticks on every dine-in check.* event over the shared /ws/orders socket (H-5), so the
  // floor refreshes live off the SAME subscription the Orders/Kitchen boards use — no new poll.
  const { checkRev } = useOrderAlerts();
  const [, setNowTick] = useState(0);   // 30s UI clock so a tile's age ticks without a refetch
  const [sps, setSps] = useState([]);
  const [checks, setChecks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);     // running-bill view
  const [openSP, setOpenSP] = useState(null);             // "open table" sheet
  const [openForm, setOpenForm] = useState({ covers: "", steward: "" });
  const [addItemsFor, setAddItemsFor] = useState(null);   // check id → NewOrderModal (round mode)
  const [settleCheck, setSettleCheck] = useState(null);
  const [lineSheet, setLineSheet] = useState(null);       // { line, round, comped } → LineActionSheet
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSel, setMergeSel] = useState([]);
  const [billsOpen, setBillsOpen] = useState(false);      // "Today's bills" list (settled checks + orders)
  const [unpaidChecks, setUnpaidChecks] = useState([]);   // bills closed "No payment", awaiting payment
  const [chargeBusy, setChargeBusy] = useState(false);    // service-charge add/remove in flight (no double-tap)

  const load = useCallback(async () => {
    try {
      // Open AND printed checks occupy the floor (G-7): a printed bill still holds its table
      // ("Bill printed") until it is settled, reopened, or voided. Unpaid ("No payment") bills
      // are tracked separately and do NOT hold a tile — there is no "paid awaiting clearing" state.
      // allSettled, not Promise.all (Prompt U, Commit 1): one failing call must degrade its own
      // section, not take the other three down and drop the whole floor to its empty state. Each
      // section keeps its previous data on a transient failure rather than blanking.
      const [sp, ck, pr, unp] = await Promise.allSettled([
        api.get("/service-points"), api.get("/checks?status=open"),
        api.get("/checks?status=printed"), api.get("/checks?status=unpaid")]);
      if (sp.status === "fulfilled") setSps(sp.value.data.service_points || []);
      if (ck.status === "fulfilled" || pr.status === "fulfilled") {
        setChecks([...(ck.status === "fulfilled" ? (ck.value.data.checks || []) : []),
                   ...(pr.status === "fulfilled" ? (pr.value.data.checks || []) : [])]);
      }
      if (unp.status === "fulfilled") setUnpaidChecks(unp.value.data.checks || []);
      const firstFail = [sp, ck, pr, unp].find((r) => r.status === "rejected");
      if (firstFail) toast.error(formatError(firstFail.reason));
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    api.get("/staff").then(({ data }) => setStaff(data || [])).catch(() => {});
  }, [load]);
  // Live floor (H-5): a check changed somewhere (a round placed, a round marked ready/printed,
  // a bill settled) → refetch so the tile's state, total and KOT count follow within a second,
  // with no reload. Skips the initial tick (the mount effect already loaded).
  useEffect(() => { if (checkRev > 0) load(); }, [checkRev, load]);
  // A render clock so a tile's age keeps ticking between events. Not a server poll — no fetch.
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const ordering = (user?.venue?.features || {}).ordering;
  const mode = user?.venue?.ordering_mode;
  const dineInOk = ordering && (mode === "dine_in" || mode === "both");
  const defaultCovers = user?.venue?.default_covers ?? 2;
  const requireSteward = !!user?.venue?.require_steward;
  const isAdmin = user?.role === "admin";   // the empty state must not send a server to Settings (U)

  const areas = sps.filter((s) => s.level === "area" && s.active)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const allTables = sps.filter((s) => s.level === "table" && s.active);
  const tablesOf = (areaId) => sps.filter((s) => s.level === "table" && s.parent_id === areaId && s.active)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || String(a.label).localeCompare(String(b.label)));
  const checkForSp = (spId) => checks.find((c) => (c.service_point_ids || []).includes(spId));
  const labelOfSp = (spId) => (sps.find((s) => s.id === spId) || {}).label || spId;
  const stewardName = (id) => (staff.find((s) => s.id === id) || {}).name || null;
  const selected = [...checks, ...unpaidChecks].find((c) => c.id === selectedId) || null;

  // ---- actions ----
  const openTable = async () => {
    if (requireSteward && !openForm.steward) { toast.error("Assign a steward first"); return; }
    try {
      const { data } = await api.post("/checks", {
        service_point_id: openSP.id,
        covers: openForm.covers === "" ? undefined : Number(openForm.covers),
        steward_staff_id: openForm.steward || undefined,
      });
      setOpenSP(null);
      await load();
      setSelectedId(data.id);
    } catch (err) { toast.error(formatError(err)); }
  };
  const printBill = async (id, wasPrinted) => {
    try {
      await api.post(`/checks/${id}/print`);   // flip status + record the bill_printed event
      await load();
      // Actually produce the document — the same bill "Settle & print" renders. The old code
      // toasted "Bill printed" after only the status call, so staff were told a bill printed when no
      // document was ever made. Only claim success when a document was really produced; the helper
      // surfaces its own failure.
      // A bill already presented/settled before this press is a re-issue → stamp DUPLICATE (AF-3).
      const printed = await printInvoice(id, { duplicate: !!wasPrinted, meta: { document: "bill", refType: "check", refId: id } });
      if (printed) toast.success("Bill printed");
    } catch (err) { toast.error(formatError(err)); }
  };
  // Print the kitchen docket for ONE round (G-8). The server stamps printed_at the first time and
  // tells us whether this is a reprint; the KOT (no prices) is rendered from the round we already
  // hold. Reload so a later print of the same round shows ** REPRINT **.
  const printRoundKot = async (chk, rnd) => {
    try {
      const { data } = await api.post(`/checks/${chk.id}/rounds/${rnd.round_no}/print-kot`);
      await printRoundKOT({
        kotNumber: rnd.kot_number,
        placedAt: rnd.placed_at,
        items: rnd.items || [],
        venueName: user?.venue?.name || "",
        location: (chk.service_point_ids || []).map(labelOfSp).join(" + "),
        reprint: !!data.reprint,
        widthMm: user?.venue?.receipt_width_mm || 80,
        refType: "check", refId: `${chk.id}:${rnd.round_no}`,
      });
      load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const voidCheck = async (chk) => {
    const reason = window.prompt("Reason for voiding this bill?");
    if (!reason || !reason.trim()) return;
    // A printed bill moves real money, so voiding it needs the manager override PIN when the venue
    // has one set (Z-5). Voiding an unprinted bill is just clearing a table — no PIN.
    let override_pin;
    if (chk.status === "printed" && user?.venue?.has_override_pin) {
      const pin = window.prompt("Manager override PIN to void a printed bill:");
      if (!pin || !pin.trim()) return;
      override_pin = pin.trim();
    }
    try { await api.post(`/checks/${chk.id}/void`, { void_reason: reason.trim(), override_pin }); setSelectedId(null); await load(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const reopenCheck = async (id) => {
    const reason = window.prompt("Reason for reopening this bill?");
    if (!reason || !reason.trim()) return;
    try { await api.post(`/checks/${id}/reopen`, { reopen_reason: reason.trim() }); await load(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const doMerge = async (targetId) => {
    const sources = mergeSel.filter((x) => x !== targetId);
    if (!sources.length) { toast.error("Pick at least one other bill to merge in"); return; }
    try {
      await api.post(`/checks/${targetId}/merge`, { source_check_ids: sources });
      setMergeMode(false); setMergeSel([]); await load();
      toast.success("Bills merged");
    } catch (err) { toast.error(formatError(err)); }
  };
  // ---- adjustments (service charge add/remove + discount) ----
  // One handler for a charge row: remove when adjId is given, else add. Guards against a double-tap
  // (chargeBusy) and always re-renders from the API response (load) — never computes a total here.
  const toggleCharge = async (checkId, adjId, chargeId) => {
    if (chargeBusy) return;
    setChargeBusy(true);
    try {
      if (adjId) await api.delete(`/checks/${checkId}/adjustments/${adjId}`);
      else await api.post(`/checks/${checkId}/adjustments`, { type: "charge", charge_id: chargeId });
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setChargeBusy(false); }
  };
  const removeAdjustment = async (id, adjId) => {
    try { await api.delete(`/checks/${id}/adjustments/${adjId}`); await load(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const applyDiscount = async (id, { mode, value, reason }) => {
    // Retry once with the override PIN if the server says this discount crosses the threshold.
    let override_pin;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await api.post(`/checks/${id}/adjustments`, { type: "discount", mode, value, reason, override_pin });
        await load();
        toast.success("Discount applied");
        return true;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 && attempt === 0) {
          override_pin = window.prompt("This discount needs the manager override PIN:");
          if (!override_pin) return false;
          continue;   // retry with the PIN
        }
        toast.error(formatError(err));
        return false;
      }
    }
    return false;
  };

  if (!dineInOk) {
    return <AppLayout><div className="max-w-[900px] mx-auto px-4 py-10 text-sm text-[#A0A0A0]">Dine-in is not enabled for this venue. Turn on an order type of Dine-in or Both in Settings → Ordering.</div></AppLayout>;
  }
  if (loading) {
    return <AppLayout><div className="max-w-[900px] mx-auto px-4 py-10 text-sm text-[#A0A0A0]">Loading tables…</div></AppLayout>;
  }

  // ---------- Running Bill view ----------
  if (selected) {
    const b = selected.bill || {};
    const adjustments = selected.adjustments || [];
    const rows = billRows(b, adjustments);
    const isOpen = selected.status === "open";
    // line_id → comp adjustment (a comped line shows struck at ₹0 and is undo-able).
    const compByLine = {};
    adjustments.forEach((a) => { if (a.type === "comp" && a.line_id) compByLine[a.line_id] = a; });
    // Charge rows to show in the breakdown: ONE per active charge definition that applies to this
    // check (every check is dine_in in this build), whether or not it is currently applied — driven
    // by the venue's charge_definitions, never a hardcoded id, so a venue's sc1/5%/fixed/several all
    // work and a takeaway-only definition (pk1) never shows. The applied amount comes from the API
    // bill.charges (matched by label); never computed here.
    const billCharges = Array.isArray(b.charges) ? b.charges : [];
    const amountForLabel = (label) => {
      const c = billCharges.find((x) => x.label === label);
      return c ? (Number(c.amount) || 0) : 0;
    };
    const chargeDefs = (user?.venue?.charge_definitions || []).filter((c) =>
      c.active !== false && (!Array.isArray(c.applies_to) || c.applies_to.length === 0 || c.applies_to.includes("dine_in")));
    const chargeDefIds = new Set(chargeDefs.map((d) => d.id));
    const chargeRenderRows = [
      ...chargeDefs.map((def) => {
        const adj = adjustments.find((a) => a.type === "charge" && a.charge_id === def.id);
        return { key: def.id, chargeId: def.id, label: def.label, mode: def.mode, value: def.value,
                 adj, amount: adj ? amountForLabel(def.label) : null };
      }),
      // An applied charge whose definition no longer qualifies (deactivated / takeaway-only now):
      // keep a remove-only row so it can still be taken off.
      ...adjustments.filter((a) => a.type === "charge" && !chargeDefIds.has(a.charge_id)).map((adj) => (
        { key: adj.id, chargeId: adj.charge_id, label: adj.label, mode: adj.mode, value: adj.value,
          adj, amount: amountForLabel(adj.label) }
      )),
    ];
    const discountAdjs = adjustments.filter((a) => a.type === "discount");
    const plural = (n) => `${n} item${n === 1 ? "" : "s"}`;

    return (
      <AppLayout>
        <div className="max-w-[760px] mx-auto px-4 md:px-6 py-6">
          <button onClick={() => setSelectedId(null)} className="flex items-center gap-1 text-sm text-[#A0A0A0] hover:text-white mb-4">
            <ChevronLeft size={16} /> All tables
          </button>

          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-xl font-black text-white">
                  {(selected.service_point_ids || []).map(labelOfSp).join(" + ")}
                </div>
                <div className="text-xs text-[#A0A0A0] mt-1 flex items-center gap-3 flex-wrap">
                  <span>Bill #{selected.check_number}</span>
                  {/* The attached customer, visible before the cashier opens settle (Prompt Y). */}
                  {selected.customer_name && <span className="flex items-center gap-1 text-[#D78CFF]">👤 {selected.customer_name}</span>}
                  {selected.covers ? <span className="flex items-center gap-1"><Users size={12} />{selected.covers} covers</span> : null}
                  {stewardName(selected.steward_staff_id) && <span>{stewardName(selected.steward_staff_id)}</span>}
                  <span className="flex items-center gap-1"><Clock size={12} />{minsSince(selected.opened_at)}</span>
                  <span className="uppercase tracking-wider">{selected.status}</span>
                </div>
              </div>
              <div className="text-2xl font-black text-[#9D00FF]">₹{b.rounded_total ?? Math.round(b.total || 0)}</div>
            </div>
          </div>

          {/* Rounds — expanded by default; cancelled (ghost) rounds never render (Commit 1) */}
          <div className="flex flex-col gap-2 mb-4">
            {(selected.rounds || []).filter((rnd) => !rnd.cancelled).map((rnd) => (
              <details key={rnd.round_no} open className="bg-[#0F0F0F] border border-white/5 rounded-xl px-4 py-3">
                <summary className="cursor-pointer text-sm font-semibold text-white flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate">KOT {rnd.kot_number} · {new Date(rnd.placed_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} · {plural((rnd.items || []).filter((i) => !i.cancelled).length)}{rnd.origin === "qr" ? " · QR" : ""}</span>
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); printRoundKot(selected, rnd); }}
                    className="shrink-0 flex items-center gap-1 text-[11px] font-bold text-[#A0A0A0] hover:text-white border border-white/10 hover:border-white/30 rounded-lg px-2 py-1 min-h-[32px]">
                    <Printer size={12} /> {rnd.printed_at ? "Reprint KOT" : "Print KOT"}
                  </button>
                </summary>
                <div className="mt-2 flex flex-col">
                  {(rnd.items || []).map((it) => {
                    const comped = compByLine[it.line_id];
                    const cancelled = !!it.cancelled;   // tombstoned (G-2): struck, out of the bill, no actions
                    return (
                      <div key={it.line_id} className="flex items-start gap-1 py-1 border-t border-white/5 first:border-t-0">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm flex items-center gap-2 flex-wrap">
                            <span className={(comped || cancelled) ? "line-through text-[#777]" : "text-[#E0E0E0]"}>{it.qty}× {it.name}{it.variant_label ? ` (${it.variant_label})` : ""}</span>
                            {cancelled
                              ? <span className="text-[9px] font-bold uppercase tracking-wide bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded">Cancelled</span>
                              : comped && <span className="text-[9px] font-bold uppercase tracking-wide bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded">Free</span>}
                          </div>
                          {cancelled && it.cancelled_reason && <div className="text-[11px] text-[#C06868] pl-0">{it.cancelled_reason}</div>}
                          {(it.addons || []).map((a, j) => (
                            <div key={j} className="text-[11px] text-[#808080] pl-4">+ {a.name}</div>
                          ))}
                          {/* Per-item note (Prompt T, Commit 4) — the cashier needs it on the one
                              screen they are looking at while the guest asks "did you put no onion?".
                              Same amber treatment as the Orders board card. */}
                          {it.note && !cancelled && (
                            <div className="text-[11px] text-amber-400">· {it.note}</div>
                          )}
                        </div>
                        <div className="text-sm text-right whitespace-nowrap">
                          {cancelled
                            ? <span className="line-through text-[#777]">₹{(it.line_total || 0).toFixed(2)}</span>
                            : comped
                              ? <><span className="line-through text-[#777] mr-1">₹{(it.line_total || 0).toFixed(2)}</span><span className="text-white">₹0.00</span></>
                              : <span className="text-[#E0E0E0]">₹{(it.line_total || 0).toFixed(2)}</span>}
                        </div>
                        {isOpen && !cancelled && (
                          <button onClick={() => setLineSheet({ line: it, round: rnd, comped: !!comped })}
                            className="w-11 h-11 -my-1 flex items-center justify-center text-[#A0A0A0] hover:text-white shrink-0" aria-label="Item actions">
                            <MoreVertical size={18} />
                          </button>
                        )}
                        {isOpen && cancelled && <div className="w-11 shrink-0" aria-hidden="true" />}
                      </div>
                    );
                  })}
                </div>
              </details>
            ))}
            {(selected.rounds || []).filter((rnd) => !rnd.cancelled).length === 0 && <p className="text-xs text-[#555]">No rounds yet — add items to start the bill.</p>}
          </div>

          {/* Bill breakdown — every figure from the API bill object; service charge carries a control */}
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-4 font-mono text-sm">
            {rows.map((r, i) => (
              <div key={i} className="flex justify-between text-[#C0C0C0] py-0.5">
                <span>{r.label}</span><span>{r.neg ? "-" : ""}₹{Math.abs(r.amt).toFixed(2)}</span>
              </div>
            ))}
            {/* A row per applicable charge definition — add OR remove in one tap, no confirmation
                (reversible, and the thing guests argue about). Controls disabled off-open or in flight. */}
            {chargeRenderRows.map((r) => (
              <div key={r.key} className="flex justify-between items-center text-[#C0C0C0] py-0.5 min-h-[44px]">
                <span>{r.label} ({r.mode === "fixed" ? `₹${r.value}` : `${r.value}%`})</span>
                {r.adj
                  ? <span className="flex items-center gap-2">₹{(r.amount || 0).toFixed(2)}
                      <button disabled={!isOpen || chargeBusy} onClick={() => toggleCharge(selected.id, r.adj.id, null)}
                        className="font-sans font-semibold text-xs text-red-400 hover:text-red-300 disabled:opacity-40 rounded-lg px-3 min-h-[44px]">remove</button></span>
                  : <button disabled={!isOpen || chargeBusy} onClick={() => toggleCharge(selected.id, null, r.chargeId)}
                      className="font-sans font-semibold text-xs text-[#9D00FF] hover:text-[#B94DFF] disabled:opacity-40 rounded-lg px-3 min-h-[44px]">add</button>}
              </div>
            ))}
            {Number(b.round_off) !== 0 && (
              <div className="flex justify-between text-[#C0C0C0] py-0.5">
                <span>Round off</span><span>{Number(b.round_off) < 0 ? "-" : ""}₹{Math.abs(Number(b.round_off) || 0).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-white font-bold border-t border-white/10 mt-1 pt-1">
              <span>Total</span><span>₹{b.rounded_total ?? Math.round(b.total || 0)}</span>
            </div>
          </div>

          {/* Discounts applied — each with a way to see and undo it (open bills only) */}
          {isOpen && (
            <DiscountBar discounts={discountAdjs}
              onRemove={(aid) => removeAdjustment(selected.id, aid)}
              onApplyDiscount={(d) => applyDiscount(selected.id, d)} />
          )}

          {/* Buttons — destructive actions are never the primary button */}
          <div className="flex flex-wrap gap-2 items-center">
            {isOpen && <button onClick={() => setAddItemsFor(selected.id)} className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-sm font-bold rounded-lg px-4 py-2.5 min-h-[44px]"><Plus size={15} /> Add items</button>}
            <button onClick={() => printBill(selected.id, selected.status !== "open")} className="flex items-center gap-1.5 bg-white/5 border border-white/10 text-white text-sm font-semibold rounded-lg px-4 py-2.5 min-h-[44px]"><Printer size={15} /> Print bill</button>
            {(isOpen || selected.status === "printed") && <button onClick={() => setSettleCheck(selected)} className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg px-4 py-2.5 min-h-[44px]"><CreditCard size={15} /> Settle</button>}
            {selected.status === "unpaid" && <button onClick={() => setSettleCheck(selected)} className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg px-4 py-2.5 min-h-[44px]"><CreditCard size={15} /> Record payment</button>}
            {selected.status === "printed" && <button onClick={() => reopenCheck(selected.id)} className="text-sm text-[#A0A0A0] hover:text-white rounded-lg px-3 py-2.5 min-h-[44px]">Reopen</button>}
            {(isOpen || selected.status === "printed") && <button onClick={() => voidCheck(selected)} className="text-sm text-red-400 hover:text-red-300 rounded-lg px-3 py-2.5 min-h-[44px] ml-auto">Void</button>}
          </div>
        </div>

        {addItemsFor && (
          <NewOrderModal checkId={addItemsFor} servicePointId={(selected.service_point_ids || [])[0]}
            onClose={() => setAddItemsFor(null)}
            onRoundAdded={() => { setAddItemsFor(null); load(); }} onOrderPlaced={() => {}} />
        )}
        {lineSheet && (
          <LineActionSheet checkId={selected.id} line={lineSheet.line} comped={lineSheet.comped}
            compAdjId={lineSheet.comped ? (compByLine[lineSheet.line.line_id] || {}).id : null}
            onClose={() => setLineSheet(null)}
            onDone={() => { setLineSheet(null); load(); }} />
        )}
        {settleCheck && (
          <SettleSheet check={settleCheck} venue={user?.venue} onClose={() => setSettleCheck(null)}
            onSettled={() => { setSettleCheck(null); setSelectedId(null); load(); }} />
        )}
      </AppLayout>
    );
  }

  // ---------- Tile grid ----------
  return (
    <AppLayout>
      <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <h1 className="text-3xl font-black tracking-tight">Tables</h1>
          <div className="flex items-center gap-2">
            {/* The day's bills — the only door to a settled check once it has left the floor, and to
                the reprint of one. Floor staff, not admin-only. */}
            <button onClick={() => setBillsOpen(true)}
              className="flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 min-h-[44px] border border-white/10 text-[#A0A0A0] hover:text-white">
              <Receipt size={15} /> Today's bills
            </button>
            {allTables.length > 0 && (
              <button onClick={() => { setMergeMode((m) => !m); setMergeSel([]); }}
                className={`flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 min-h-[44px] border ${mergeMode ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0] hover:text-white"}`}>
                <GitMerge size={15} /> {mergeMode ? "Cancel merge" : "Merge"}
              </button>
            )}
          </div>
        </div>

        {/* P2-4 — a venue that turned on dine-in later may have areas but no tables (seeding only
            creates tables when dine-in was already on). Explain how to add them instead of a blank page. */}
        {allTables.length === 0 ? (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-8 text-center max-w-[560px] mx-auto mt-6">
            <div className="w-12 h-12 rounded-full bg-[#9D00FF]/15 flex items-center justify-center mx-auto mb-4">
              <LayoutGrid size={22} className="text-[#9D00FF]" />
            </div>
            <div className="text-lg font-bold text-white mb-1">No tables yet</div>
            {isAdmin ? (
              <>
                <p className="text-sm text-[#A0A0A0] mb-5">
                  Dine-in bills open on a table. Add and name your tables in Settings → Ordering →
                  Service Points, then they'll show up here.
                </p>
                <button onClick={() => navigate("/settings")}
                  className="inline-flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-sm font-bold rounded-lg px-4 py-2.5">
                  Add tables in Settings
                </button>
              </>
            ) : (
              <p className="text-sm text-[#A0A0A0]">
                No tables are set up here yet. Ask an admin to add them — they'll show up as soon as they do.
              </p>
            )}
          </div>
        ) : (
        <>

        {/* Bills closed with no payment, awaiting a later payment (Commit 4) */}
        {unpaidChecks.length > 0 && (
          <div className="mb-5">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-300/80 mb-2">Awaiting payment</div>
            <div className="flex flex-col gap-2">
              {unpaidChecks.map((c) => (
                <button key={c.id} onClick={() => setSelectedId(c.id)}
                  className="flex items-center justify-between text-left bg-amber-500/5 border border-amber-500/25 rounded-xl px-4 py-3 min-h-[44px]">
                  <span className="text-sm text-white">Bill #{c.check_number}{c.customer_name ? ` · ${c.customer_name}` : ""}
                    <span className="text-[11px] text-amber-300/70 ml-2">{c.unpaid_reason}</span></span>
                  <span className="text-sm font-black text-amber-300">₹{(c.bill || {}).rounded_total ?? Math.round((c.bill || {}).total || 0)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {mergeMode && (
          <div className="bg-[#9D00FF]/10 border border-[#9D00FF]/30 rounded-xl px-4 py-3 mb-4 text-sm text-white flex items-center justify-between flex-wrap gap-2">
            <span>Pick the occupied tables to merge, then confirm on one of them.</span>
            {mergeSel.length >= 2 && (
              <button onClick={() => doMerge(mergeSel[0])} className="bg-[#9D00FF] text-white font-bold rounded-lg px-4 py-2">
                Merge into {labelOfSp((checks.find((c) => c.id === mergeSel[0])?.service_point_ids || [])[0])}
              </button>
            )}
          </div>
        )}

        {areas.map((area) => (
          <div key={area.id} className="mb-6">
            <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2">{area.label}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
              {tablesOf(area.id).map((t) => {
                const chk = checkForSp(t.id);
                const occupied = !!chk;
                const spids = (chk && chk.service_point_ids) || [];
                // A merged check spans several tables but is ONE bill. Draw the full tile only on
                // its primary (first) table; the other member tables show a muted "merged into …"
                // placeholder so the floor still accounts for every table without doubling the
                // running total (P1-2).
                const isPrimary = occupied && spids[0] === t.id;
                const isMergedMember = occupied && !isPrimary;
                const selForMerge = mergeSel.includes(chk?.id);
                const st = TILE_STATE[tileStateKey(chk)];   // one of the 5 kitchen states (G-7)
                const onTileClick = () => {
                  if (mergeMode) {
                    // Only open bills can be merged — a printed bill is awaiting payment.
                    if (!occupied || isMergedMember || chk.status !== "open") return;
                    setMergeSel((p) => p.includes(chk.id) ? p.filter((x) => x !== chk.id) : [...p, chk.id]);
                    return;
                  }
                  if (occupied) setSelectedId(chk.id);   // a member tile opens the same merged bill
                  else { setOpenForm({ covers: String(defaultCovers), steward: "" }); setOpenSP(t); }
                };
                if (isMergedMember) {
                  return (
                    <button key={t.id} onClick={onTileClick}
                      className="text-left rounded-xl p-3 border border-dashed border-[#9D00FF]/25 bg-[#140A1A] min-h-[92px]">
                      <div className="text-sm font-bold text-[#A0A0A0]">{t.label}</div>
                      <div className="text-[11px] text-[#9D00FF]/80 mt-1 flex items-center gap-1">
                        <GitMerge size={11} /> Merged into {labelOfSp(spids[0])}
                      </div>
                    </button>
                  );
                }
                return (
                  <button key={t.id} onClick={onTileClick}
                    className={`text-left rounded-xl p-3 border transition min-h-[92px] ${
                      selForMerge ? "border-[#9D00FF] bg-[#9D00FF]/20" : st.tileCls}`}>
                    <div className="flex items-center justify-between gap-1.5">
                      <div className="text-sm font-bold text-white truncate">
                        {occupied ? spids.map(labelOfSp).join(" + ") : t.label}
                      </div>
                      <span className={`shrink-0 text-[10px] font-black uppercase tracking-wide ${st.wordCls}`}>{st.label}</span>
                    </div>
                    {occupied ? (
                      <>
                        <div className="text-lg font-black text-[#9D00FF] mt-1">₹{(chk.bill || {}).rounded_total ?? Math.round((chk.bill || {}).total || 0)}</div>
                        <div className="text-[11px] text-[#A0A0A0] mt-0.5">{(chk.rounds || []).filter((r) => !r.cancelled).length} KOTs · {minsSince(chk.opened_at)}</div>
                        {/* A known guest, quietly (Prompt Y) — a name, not a badge competing with the state + total. */}
                        {chk.customer_name && <div className="text-[11px] text-[#D78CFF] mt-0.5 truncate">👤 {chk.customer_name}</div>}
                        {stewardName(chk.steward_staff_id) && <div className="text-[11px] text-[#A0A0A0] uppercase tracking-wide">{stewardName(chk.steward_staff_id)}</div>}
                      </>
                    ) : (
                      <div className="text-[11px] text-[#555] mt-1">tap to open</div>
                    )}
                  </button>
                );
              })}
              {tablesOf(area.id).length === 0 && <div className="text-xs text-[#555] py-2">{isAdmin ? "No tables — add some in Settings → Ordering → Service Points." : "No tables in this area yet."}</div>}
            </div>
          </div>
        ))}
        </>
        )}
      </div>

      {/* Open-table sheet */}
      {openSP && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }} onClick={() => setOpenSP(null)}>
          <div className="w-[min(400px,96vw)] bg-[#0A0A0A] border border-white/10 rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className="font-bold text-white">Open {openSP.label}</span>
              <button onClick={() => setOpenSP(null)} className="text-[#A0A0A0]"><XCircle size={18} /></button>
            </div>
            <label className="text-xs text-[#A0A0A0] block mb-1">Covers</label>
            <input type="number" min={1} max={20} value={openForm.covers}
              onChange={(e) => setOpenForm((f) => ({ ...f, covers: e.target.value }))}
              className="w-24 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D00FF] focus:outline-none mb-3" />
            <label className="text-xs text-[#A0A0A0] block mb-1">Steward {requireSteward && <span className="text-red-400">*</span>}</label>
            <select value={openForm.steward} onChange={(e) => setOpenForm((f) => ({ ...f, steward: e.target.value }))}
              className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D00FF] focus:outline-none mb-4">
              <option value="">— none —</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button onClick={openTable} className="w-full bg-[#9D00FF] hover:bg-[#8C00E6] text-white font-bold rounded-lg py-2.5">Open table</button>
          </div>
        </div>
      )}

      {billsOpen && <DayBillsSheet onClose={() => setBillsOpen(false)} />}
    </AppLayout>
  );
}

// ---- Today's bills: settled dine-in checks AND settled counter orders, newest first, each with a
// reprint. The only route to a bill once it has left the floor (its table went back to Free) or the
// board. Reprint goes through the shared print helper (hidden iframe) and the /invoice/{id} route,
// which renders a check or an order alike; the helper surfaces its own failure.
function DayBillsSheet({ onClose }) {
  const [bills, setBills] = useState(null);     // null = loading
  const [printingId, setPrintingId] = useState(null);

  useEffect(() => {
    api.get("/day-bills")
      .then(({ data }) => setBills(data.bills || []))
      .catch((err) => { toast.error(formatError(err)); setBills([]); });
  }, []);

  const reprint = async (id) => {
    setPrintingId(id);
    try { await printInvoice(id, { duplicate: true, meta: { document: "bill", refType: "check", refId: id } }); }   // Today's-bills reprint is always a re-issue (AF-3)
    finally { setPrintingId(null); }
  };
  const fmtTime = (iso) => iso
    ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
    : "";
  const typeLabel = (t) => t === "dine_in" ? "Dine-in" : t === "room" ? "Room" : "Takeaway";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div className="w-[min(560px,96vw)] max-h-[85vh] flex flex-col bg-[#0A0A0A] border border-white/10 rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <span className="font-bold text-white">Today's bills</span>
          <button onClick={onClose} className="text-[#A0A0A0] min-h-[44px] min-w-[44px] flex items-center justify-center -mr-2"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto px-3 py-1" style={{ minHeight: 0 }}>
          {bills === null ? (
            <div className="text-center text-sm text-[#A0A0A0] py-10">Loading…</div>
          ) : bills.length === 0 ? (
            <div className="text-center text-sm text-[#555] py-10">No bills settled yet today.</div>
          ) : (
            bills.map((b) => (
              <div key={`${b.source}-${b.id}`} className="flex items-center gap-3 px-2 py-2.5 border-b border-white/[0.04]">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">
                    <span className="font-semibold">{b.number}</span>
                    <span className="text-[#A0A0A0]"> · {typeLabel(b.order_type)}</span>
                  </div>
                  <div className="text-[11px] text-[#777] truncate">{b.customer_name} · {fmtTime(b.time)} · {b.payment_method}</div>
                </div>
                <span className="text-sm font-black text-white shrink-0">₹{Math.round(b.total || 0)}</span>
                <button onClick={() => reprint(b.id)} disabled={printingId === b.id}
                  className="shrink-0 flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 min-h-[44px] bg-white/5 border border-white/10 text-white hover:border-white/30 disabled:opacity-50">
                  <Printer size={15} /> {printingId === b.id ? "…" : "Print"}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Settle sheet: payment rows that must sum to the rounded total ----
const PAYMENT_METHODS = [
  { v: "cash", l: "Cash" }, { v: "upi", l: "UPI" }, { v: "card", l: "Card" },
  { v: "dineout", l: "Dineout" }, { v: "district", l: "District" }, { v: "eazydiner", l: "EazyDiner" },
];
const NO_PAYMENT_REASONS = ["Staff / house meal", "Management complimentary", "Guest dispute", "Guest left without paying", "House account"];

function SettleSheet({ check, venue, onClose, onSettled }) {
  // The payable figure is the server's rounded_total (half-up); never re-round it client-side (P0-3).
  const baseTarget = Number((check.bill || {}).rounded_total ?? Math.round((check.bill || {}).total || 0));
  const recordMode = check.status === "unpaid";   // recording payment on a bill closed unpaid
  const [tab, setTab] = useState("pay");            // "pay" | "nopay" (settle mode only)
  const [tip, setTip] = useState("");
  const [custPhone, setCustPhone] = useState(check.customer_phone || "");
  const [custName, setCustName] = useState(check.customer_name || "");
  // Prompt Y: a customer attached to the check turns settle from an interrogation into a
  // confirmation — we state who they are and offer redemption directly, with no phone to type.
  const attached = !!(check.customer_phone || "").trim();
  const [npReason, setNpReason] = useState("");
  const [npPin, setNpPin] = useState("");
  const [saving, setSaving] = useState(false);

  // --- Loyalty redemption (Prompt R, Commit 2). Settle mode only — record-payment on an unpaid
  //     bill has no redemption path server-side. The customer-mobile field below is the hook. ---
  const [rPreview, setRPreview] = useState(null);   // {redeemable, balance, earned_today, blocked} | null
  const [rPoints, setRPoints] = useState("");
  const [rStage, setRStage] = useState("idle");     // "idle" | "otp" | "verified"
  const [rPendingId, setRPendingId] = useState(null);
  const [rOtp, setROtp] = useState("");
  const [rNet, setRNet] = useState(null);           // server's post-redemption rounded_total (initiate)
  const [rPointsApplied, setRPointsApplied] = useState(0);
  const [rBusy, setRBusy] = useState(false);
  const [rResends, setRResends] = useState(0);
  const [showOverride, setShowOverride] = useState(false);
  const [overridePin, setOverridePin] = useState("");

  const redeemed = rStage === "verified" && rNet != null;
  const redeemable = Number((rPreview || {}).redeemable || 0);
  // EVERYTHING downstream keys off `target`: the instant a redemption verifies, the payable drops and
  // cash received, change, split rows and remaining all follow it (Prompt R — the one thing that must
  // never lag). The server's net_payable is GST-correct (redemption is pre-tax); we never derive it here.
  const target = redeemed ? Number(rNet) : baseTarget;

  const [rows, setRows] = useState([{ method: "cash", amount: String(baseTarget), reference: "", received: "" }]);
  // Reset the tender to one row for the current payable whenever it changes (a verified redemption),
  // so a stale split can't leave the remaining wrong.
  useEffect(() => {
    setRows([{ method: "cash", amount: String(target), reference: "", received: "" }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  // Preview what this customer can redeem once a full phone is entered (before any OTP).
  useEffect(() => {
    if (recordMode) { setRPreview(null); return; }
    const phone = (custPhone || "").replace(/\D/g, "");
    if (phone.length !== 10) { setRPreview(null); return; }
    if (rStage !== "idle") return;   // an OTP is in flight or verified — leave it alone
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/staff/checks/${check.id}/redemption-preview`, { customer_phone: phone });
        if (!cancelled) setRPreview(data);
      } catch { if (!cancelled) setRPreview(null); }   // 404 no customer → silent; the field is still for the e-bill
    })();
    return () => { cancelled = true; };
  }, [custPhone, recordMode, check.id, rStage]);

  const paid = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const remaining = Math.round((target - paid) * 100) / 100;
  const change = rows.reduce((s, r) => s + (r.method === "cash" && Number(r.received) > Number(r.amount) ? Number(r.received) - Number(r.amount) : 0), 0);

  const paymentPayload = () => rows.filter((r) => Number(r.amount) > 0).map((r, i) => ({
    method: r.method, amount: Number(r.amount), reference: (r.reference || "").trim() || undefined,
    amount_received: r.method === "cash" && r.received !== "" ? Number(r.received) : undefined,
    tip: i === 0 && Number(tip) > 0 ? Number(tip) : undefined,   // tip rides the first row; not in the total
  }));

  const sendOtp = async () => {
    const pts = parseInt(rPoints, 10);
    if (!(pts > 0)) { toast.error("Enter points to redeem"); return; }
    if (pts > redeemable) { toast.error(`Only ${redeemable} points can be redeemed on this bill`); return; }
    setRBusy(true);
    try {
      const { data } = await api.post("/staff/redeem/initiate", {
        customer_phone: (custPhone || "").replace(/\D/g, ""), points: pts, check_id: check.id,
      });
      setRPendingId(data.pending_id); setRNet(data.net_payable); setRPointsApplied(pts);
      setRStage("otp"); setRResends(0);
      toast.success("OTP sent to the customer");
    } catch (err) { toast.error(formatError(err)); }
    finally { setRBusy(false); }
  };

  const resendOtp = async () => {
    setRBusy(true);
    try {
      await api.post("/staff/redeem/resend-otp", { pending_id: rPendingId });
      setRResends((n) => n + 1); toast.success("OTP resent");
    } catch (err) { toast.error(formatError(err)); }
    finally { setRBusy(false); }
  };

  const verifyOtp = async () => {
    const code = (rOtp || "").replace(/\D/g, "");
    if (code.length < 4) { toast.error("Enter the OTP"); return; }
    setRBusy(true);
    try {
      await api.post("/staff/redeem/verify-otp", { pending_id: rPendingId, otp_code: code });
      setRStage("verified"); toast.success(`Redeemed ${rPointsApplied} points`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setRBusy(false); }
  };

  const overrideOtp = async () => {
    const pin = (overridePin || "").replace(/\D/g, "");
    if (pin.length !== 4) { toast.error("Enter the 4-digit override PIN"); return; }
    setRBusy(true);
    try {
      await api.post("/staff/redeem/manager-override", { pending_id: rPendingId, manager_pin: pin });
      setRStage("verified"); setShowOverride(false);
      toast.success(`Redeemed ${rPointsApplied} points (manager override)`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setRBusy(false); }
  };

  const cancelRedeem = () => {
    // Abandon the pending — an unconsumed verified redemption is reversed by the cleanup job (do not
    // add a second reversal, per spec). Reset the UI so the cashier can settle at the full total.
    setRStage("idle"); setRPendingId(null); setROtp(""); setRNet(null); setRPointsApplied(0);
    setRResends(0); setShowOverride(false); setOverridePin(""); setRPoints("");
  };

  const doSettle = async (after) => {
    if (remaining !== 0) { toast.error(`₹${remaining.toFixed(2)} remaining — payments must total ₹${target}`); return; }
    const ebillPhone = (custPhone || "").replace(/\D/g, "");
    if (after === "ebill" && ebillPhone.length !== 10) { toast.error("Enter a 10-digit mobile number to e-bill"); return; }
    setSaving(true);
    try {
      const endpoint = recordMode ? `/checks/${check.id}/record-payment` : `/checks/${check.id}/settle`;
      const body = recordMode ? { payments: paymentPayload() }
        : { payments: paymentPayload(), customer_phone: custPhone.trim() || undefined, customer_name: custName.trim() || undefined,
            redemption_pending_id: redeemed ? rPendingId : undefined };
      await api.post(endpoint, body);
      const { data } = await api.get(`/checks/${check.id}`);
      // Produce the bill through the shared helper (hidden iframe). It reports its own failure, so a
      // settle that couldn't print is never mistaken for one that did.
      if (after === "print") await printInvoice(check.id, { meta: { document: "bill", refType: "check", refId: check.id } });
      if (after === "ebill") {
        await api.post(`/checks/${check.id}/send-ebill`, { phone: ebillPhone });
        toast.success("Settled — bill sent on WhatsApp");
      } else {
        toast.success(data.invoice_number ? `Settled — invoice ${data.invoice_number}` : "Settled");
      }
      onSettled();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  const doNoPayment = async () => {
    const reason = npReason === "Other" ? "" : npReason;
    if (!reason) { toast.error("Pick a reason"); return; }
    setSaving(true);
    try {
      await api.post(`/checks/${check.id}/mark-unpaid`, {
        reason, override_pin: npPin || undefined,
        customer_phone: custPhone.trim() || undefined, customer_name: custName.trim() || undefined,
      });
      toast.success("Bill closed — no payment");
      onSettled();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div className="w-full sm:w-[min(460px,96vw)] max-h-[92vh] overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <span className="font-bold text-white">{recordMode ? "Record payment" : "Settle"} · ₹{target}</span>
          <button onClick={onClose} className="text-[#A0A0A0] w-11 h-11 flex items-center justify-center -mr-2"><XCircle size={18} /></button>
        </div>

        {!recordMode && (
          <div className="flex gap-1 bg-white/5 rounded-lg p-0.5 mb-4">
            {[["pay", "Take payment"], ["nopay", "No payment"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)}
                className={`flex-1 text-xs font-semibold rounded-md py-2 min-h-[40px] ${tab === k ? "bg-[#9D00FF]/30 text-white" : "text-[#A0A0A0]"}`}>{l}</button>
            ))}
          </div>
        )}

        {(recordMode || tab === "pay") ? (
          <>
            <div className="flex flex-col gap-2 mb-2">
              {rows.map((r, i) => (
                <div key={i} className="flex flex-col gap-2 bg-[#0F0F0F] border border-white/5 rounded-lg p-2">
                  <div className="flex gap-2 items-center">
                    <select value={r.method} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, method: e.target.value } : x))}
                      className="bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-2 text-white text-sm min-h-[44px] focus:border-[#9D00FF] focus:outline-none">
                      {PAYMENT_METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                    <input type="number" min={0} value={r.amount} placeholder="Amount"
                      onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                      className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm min-h-[44px] focus:border-[#9D00FF] focus:outline-none" />
                    {rows.length > 1 && <button onClick={() => setRows((p) => p.filter((_, j) => j !== i))} className="text-[#A0A0A0] hover:text-red-400 w-11 h-11 flex items-center justify-center"><XCircle size={16} /></button>}
                  </div>
                  {r.method === "cash" && (
                    <input type="number" min={0} value={r.received} placeholder="Cash received (optional)"
                      onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, received: e.target.value } : x))}
                      className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm min-h-[44px] focus:border-[#9D00FF] focus:outline-none" />
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => setRows((p) => [...p, { method: "upi", amount: String(remaining > 0 ? remaining : 0), reference: "", received: "" }])}
              className="text-[11px] font-semibold text-[#9D00FF] hover:text-[#B94DFF] mb-3">+ Split payment</button>

            {change > 0 && (
              <div className="flex justify-between items-center bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 mb-3">
                <span className="text-sm text-emerald-200">Return to customer</span>
                <span className="text-xl font-black text-emerald-300">₹{change.toFixed(0)}</span>
              </div>
            )}

            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-[#A0A0A0] w-20">Tip</span>
              <input type="number" min={0} value={tip} onChange={(e) => setTip(e.target.value)} placeholder="0"
                className="w-28 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm min-h-[44px] focus:border-[#9D00FF] focus:outline-none" />
              <span className="text-[11px] text-[#555]">not added to the bill or GST</span>
            </div>

            {/* Customer. Attached (captured at the table) → a confirmation of who they are and what
                they have; no typing. Not attached → the phone field exactly as before, the cashier
                fallback: typing a number both attaches it (at settle) and offers redemption. */}
            {!recordMode && (
              attached ? (
                <div className="flex items-center gap-2 mb-3 bg-[#9D00FF]/10 border border-[#9D00FF]/30 rounded-lg px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-white truncate">👤 {custName || "Guest"}</div>
                    <div className="text-[11px] text-[#A0A0A0]">{custPhone}{rPreview ? ` · ${rPreview.balance} points` : ""}</div>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 mb-3">
                  <input type="tel" inputMode="numeric" value={custPhone} onChange={(e) => setCustPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="Customer mobile (for e-bill)" className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm min-h-[44px] focus:border-[#9D00FF] focus:outline-none" />
                </div>
              )
            )}

            {/* Loyalty redemption (Prompt R, Commit 2) — settle mode only; keyed off the phone above */}
            {!recordMode && rStage === "verified" && (
              <div className="flex items-center justify-between bg-[#9D00FF]/10 border border-[#9D00FF]/40 rounded-lg px-3 py-2 mb-3">
                <span className="text-sm text-[#D78CFF]">Loyalty discount <b>−₹{rPointsApplied}</b> · new total ₹{target}</span>
                <button onClick={cancelRedeem} className="text-xs text-[#A0A0A0] hover:text-white min-h-[44px] px-1">Remove</button>
              </div>
            )}
            {!recordMode && rStage === "idle" && rPreview && redeemable > 0 && (
              <div className="bg-[#0F0F0F] border border-[#9D00FF]/30 rounded-lg p-3 mb-3">
                <div className="text-xs text-[#A0A0A0] mb-1.5">🏆 {rPreview.balance} points · up to <b className="text-[#9D00FF]">{redeemable}</b> redeemable on this bill</div>
                <div className="flex gap-2">
                  <input type="number" min={1} max={redeemable} value={rPoints} onChange={(e) => setRPoints(e.target.value)}
                    placeholder="Points" className="w-24 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm min-h-[44px] focus:border-[#9D00FF] focus:outline-none" />
                  <button onClick={sendOtp} disabled={rBusy} className="flex-1 bg-[#9D00FF]/80 hover:bg-[#9D00FF] disabled:opacity-40 text-white text-sm font-semibold rounded-lg px-3 min-h-[44px]">{rBusy ? "…" : "Send OTP"}</button>
                </div>
              </div>
            )}
            {!recordMode && rStage === "idle" && rPreview && redeemable === 0 && rPreview.blocked && (
              <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3">{rPreview.blocked}</div>
            )}
            {!recordMode && rStage === "otp" && (
              <div className="bg-[#0F0F0F] border border-[#9D00FF]/30 rounded-lg p-3 mb-3">
                <div className="text-xs text-[#A0A0A0] mb-1.5">Enter the OTP sent to the customer to redeem {rPointsApplied} points</div>
                <div className="flex gap-2">
                  <input type="tel" inputMode="numeric" value={rOtp} onChange={(e) => setROtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="______" className="w-28 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm tracking-[0.3em] min-h-[44px] focus:border-[#9D00FF] focus:outline-none" />
                  <button onClick={verifyOtp} disabled={rBusy} className="flex-1 bg-[#9D00FF]/80 hover:bg-[#9D00FF] disabled:opacity-40 text-white text-sm font-semibold rounded-lg px-3 min-h-[44px]">{rBusy ? "…" : "Verify"}</button>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <button onClick={resendOtp} disabled={rBusy || rResends >= 3} className="text-[11px] text-[#9D00FF] disabled:opacity-40">Resend OTP{rResends >= 3 ? " (max reached)" : ""}</button>
                  <button onClick={cancelRedeem} className="text-[11px] text-[#A0A0A0]">Cancel</button>
                </div>
                {showOverride ? (
                  <div className="flex gap-2 mt-2">
                    <input type="password" inputMode="numeric" value={overridePin} onChange={(e) => setOverridePin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      placeholder="Manager PIN" className="w-32 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm tracking-[0.3em] min-h-[44px] focus:border-[#9D00FF] focus:outline-none" />
                    <button onClick={overrideOtp} disabled={rBusy} className="flex-1 bg-white/5 border border-white/10 disabled:opacity-40 text-white text-sm font-semibold rounded-lg px-3 min-h-[44px]">{rBusy ? "…" : "Override"}</button>
                  </div>
                ) : (
                  <button onClick={() => setShowOverride(true)} className="text-[11px] text-[#A0A0A0] mt-2 block">Customer didn't receive the OTP?</button>
                )}
              </div>
            )}

            <div className="flex justify-between text-sm mb-3">
              <span className="text-[#A0A0A0]">Remaining</span>
              <span className={remaining === 0 ? "text-emerald-400 font-bold" : "text-amber-400 font-bold"}>₹{remaining.toFixed(2)}</span>
            </div>

            {recordMode ? (
              <button onClick={() => doSettle("none")} disabled={saving || remaining !== 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-bold rounded-lg py-3 min-h-[44px]">
                {saving ? "Saving…" : "Record payment"}
              </button>
            ) : (
              <div className="flex flex-col gap-2">
                <button onClick={() => doSettle("none")} disabled={saving || remaining !== 0}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-bold rounded-lg py-3 min-h-[44px]">{saving ? "…" : "Settle"}</button>
                <div className="flex gap-2">
                  <button onClick={() => doSettle("print")} disabled={saving || remaining !== 0}
                    className="flex-1 bg-white/5 border border-white/10 disabled:opacity-40 text-white text-sm font-semibold rounded-lg py-2.5 min-h-[44px]">Settle &amp; print</button>
                  <button onClick={() => doSettle("ebill")} disabled={saving || remaining !== 0}
                    className="flex-1 bg-white/5 border border-white/10 disabled:opacity-40 text-white text-sm font-semibold rounded-lg py-2.5 min-h-[44px]">Settle &amp; e-bill</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-xs text-[#A0A0A0] mb-3">Close this bill with no money taken. An invoice is still issued; it never counts as collected revenue.</p>
            <label className="text-[11px] text-[#A0A0A0] block mb-1">Reason</label>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {NO_PAYMENT_REASONS.map((r) => (
                <button key={r} onClick={() => setNpReason(r)}
                  className={`text-xs rounded-lg px-3 py-2 min-h-[44px] border ${npReason === r ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0]"}`}>{r}</button>
              ))}
            </div>
            <label className="text-[11px] text-[#A0A0A0] block mb-1">Manager PIN</label>
            <input type="password" inputMode="numeric" value={npPin} onChange={(e) => setNpPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="••••" className="w-32 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm tracking-widest min-h-[44px] mb-3" />
            <div className="mb-3">
              <label className="text-[11px] text-[#A0A0A0] block mb-1">Attach to customer <span className="opacity-60">(optional — outstanding balance)</span></label>
              <div className="flex gap-2">
                <input type="tel" inputMode="numeric" value={custPhone} onChange={(e) => setCustPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="Mobile" className="w-36 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm min-h-[44px]" />
                <input value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="Name"
                  className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm min-h-[44px]" />
              </div>
            </div>
            <button onClick={doNoPayment} disabled={saving}
              className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white font-bold rounded-lg py-3 min-h-[44px]">
              {saving ? "…" : "Close with no payment"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Adjustments bar: opt-in service charge + bill-level discount (open running bills) ----
// Reason codes for item actions (Commit 2/3). "Other" makes the free-text field required.
const REASON_CODES = ["Wrong punch", "Guest changed their mind", "Out of stock", "Not up to standard", "Staff / house meal", "Other"];

// Discounts applied, each with a remove control, plus the apply form. (Service charge now lives in
// the bill breakdown with its own add/remove.)
function DiscountBar({ discounts, onRemove, onApplyDiscount }) {
  const [showDisc, setShowDisc] = useState(false);
  const [mode, setMode] = useState("percent");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const v = Number(value);
    if (!(v > 0)) { toast.error("Enter a discount amount"); return; }
    setBusy(true);
    const ok = await onApplyDiscount({ mode, value: v, reason: reason.trim() || undefined });
    setBusy(false);
    if (ok) { setShowDisc(false); setValue(""); setReason(""); setMode("percent"); }
  };

  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4 mb-4 flex flex-col gap-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0]">Discounts</div>
        <button onClick={() => setShowDisc((s) => !s)} className="text-xs font-semibold rounded-lg px-3 py-2 min-h-[44px] border border-white/10 text-[#A0A0A0] hover:border-white/30">Add discount</button>
      </div>
      {discounts.length === 0 && !showDisc && <div className="text-[11px] text-[#555]">No discounts on this bill.</div>}
      {discounts.map((d) => (
        <div key={d.id} className="flex items-center justify-between text-xs text-[#C0C0C0]">
          <span>{d.label || "Discount"} ({d.mode === "percent" ? d.value + "%" : "₹" + d.value})</span>
          <button onClick={() => onRemove(d.id)} className="text-red-400 hover:text-red-300 min-h-[44px] px-2">Remove</button>
        </div>
      ))}
      {showDisc && (
        <div className="flex items-end gap-2 flex-wrap border-t border-white/5 pt-3">
          <div>
            <label className="text-[10px] text-[#A0A0A0] block mb-1">Type</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:border-[#9D00FF] focus:outline-none min-h-[44px]">
              <option value="percent">%</option>
              <option value="fixed">₹</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-[#A0A0A0] block mb-1">{mode === "percent" ? "Percent" : "Amount"}</label>
            <input type="number" min={0} value={value} onChange={(e) => setValue(e.target.value)} className="w-24 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:border-[#9D00FF] focus:outline-none min-h-[44px]" />
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="text-[10px] text-[#A0A0A0] block mb-1">Reason (optional)</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:border-[#9D00FF] focus:outline-none min-h-[44px]" />
          </div>
          <button disabled={busy} onClick={submit} className="bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-xs font-bold rounded-lg px-4 py-2 min-h-[44px] disabled:opacity-50">{busy ? "…" : "Apply"}</button>
        </div>
      )}
    </div>
  );
}

// One sheet (never nested) for the three line actions: Change quantity, Cancel item, Make it free.
// Shows the reason picker + PIN together when needed, and the effect on the bill before confirming.
function LineActionSheet({ checkId, line, comped, compAdjId, onClose, onDone }) {
  const [action, setAction] = useState(null);     // "qty" | "cancel" | "free" | "uncomp"
  const [qty, setQty] = useState(line.qty || 1);
  const [reasonCode, setReasonCode] = useState("");
  const [otherText, setOtherText] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const unit = Number(line.line_total || 0) / Math.max(1, Number(line.qty || 1));

  const reason = reasonCode === "Other" ? otherText.trim() : reasonCode;
  const effect = (() => {
    if (action === "qty") {
      const delta = (Number(qty) - Number(line.qty || 1)) * unit;
      if (!delta) return null;
      return `${delta > 0 ? "₹" + delta.toFixed(2) + " will be added to" : "₹" + Math.abs(delta).toFixed(2) + " will be removed from"} this bill`;
    }
    if (action === "cancel") return `₹${Number(line.line_total || 0).toFixed(2)} will be removed from this bill`;
    if (action === "free") return `₹${Number(line.line_total || 0).toFixed(2)} will be removed from this bill`;
    return null;
  })();

  const run = async () => {
    setBusy(true);
    try {
      if (action === "uncomp") {
        await api.delete(`/checks/${checkId}/adjustments/${compAdjId}`);
      } else if (action === "qty") {
        if (Number(qty) < 1) { toast.error("Quantity must be at least 1 — use Cancel item"); setBusy(false); return; }
        await api.patch(`/checks/${checkId}/lines/${line.line_id}`, { qty: Number(qty), reason: reason || undefined, override_pin: pin || undefined });
      } else if (action === "cancel") {
        await api.delete(`/checks/${checkId}/lines/${line.line_id}`, { data: { reason: reason || undefined, override_pin: pin || undefined } });
      } else if (action === "free") {
        await api.post(`/checks/${checkId}/adjustments`, { type: "comp", line_id: line.line_id, reason: reason || undefined, override_pin: pin || undefined });
      }
      toast.success("Done");
      onDone();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };

  const ReasonAndPin = (
    <div className="flex flex-col gap-3 mt-3">
      <div>
        <label className="text-[11px] text-[#A0A0A0] block mb-1">Reason</label>
        <div className="flex flex-wrap gap-1.5">
          {REASON_CODES.map((r) => (
            <button key={r} type="button" onClick={() => setReasonCode(r)}
              className={`text-xs rounded-lg px-3 py-2 min-h-[44px] border ${reasonCode === r ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0]"}`}>{r}</button>
          ))}
        </div>
        {reasonCode === "Other" && (
          <input autoFocus value={otherText} onChange={(e) => setOtherText(e.target.value)} placeholder="Type the reason"
            className="mt-2 w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm min-h-[44px]" />
        )}
      </div>
      <div>
        <label className="text-[11px] text-[#A0A0A0] block mb-1">Manager PIN <span className="opacity-60">(if required)</span></label>
        <input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          placeholder="••••" className="w-32 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm tracking-widest min-h-[44px]" />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div className="w-full sm:w-[min(440px,96vw)] bg-[#0A0A0A] border border-white/10 rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <span className="font-bold text-white text-sm">{line.qty}× {line.name}</span>
          <button onClick={onClose} className="text-[#A0A0A0] w-11 h-11 flex items-center justify-center -mr-2"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#A0A0A0] mb-3">₹{Number(line.line_total || 0).toFixed(2)}{comped ? " · currently free" : ""}</p>

        {!action && (
          <div className="flex flex-col gap-2">
            {comped ? (
              <button onClick={() => setAction("uncomp")} className="text-left text-sm font-semibold text-white bg-white/5 border border-white/10 rounded-xl px-4 py-3 min-h-[44px]">Remove “Free” — charge for this item again</button>
            ) : (
              <>
                <button onClick={() => setAction("qty")} className="text-left text-sm font-semibold text-white bg-white/5 border border-white/10 rounded-xl px-4 py-3 min-h-[44px]">Change quantity</button>
                <button onClick={() => setAction("cancel")} className="text-left text-sm font-semibold text-amber-300 bg-white/5 border border-white/10 rounded-xl px-4 py-3 min-h-[44px]">Cancel item</button>
                <button onClick={() => setAction("free")} className="text-left text-sm font-semibold text-emerald-300 bg-white/5 border border-white/10 rounded-xl px-4 py-3 min-h-[44px]">Make it free</button>
              </>
            )}
          </div>
        )}

        {action === "qty" && (
          <div>
            <div className="flex items-center gap-3 justify-center my-4">
              <button onClick={() => setQty((q) => Math.max(1, Number(q) - 1))} className="w-11 h-11 rounded-lg bg-white/5 border border-white/10 text-white flex items-center justify-center"><Minus size={16} /></button>
              <span className="text-2xl font-black text-white w-10 text-center">{qty}</span>
              <button onClick={() => setQty((q) => Number(q) + 1)} className="w-11 h-11 rounded-lg bg-[#9D00FF]/20 border border-[#9D00FF]/40 text-[#9D00FF] flex items-center justify-center"><Plus size={16} /></button>
            </div>
            {ReasonAndPin}
          </div>
        )}
        {(action === "cancel" || action === "free") && ReasonAndPin}

        {action && (
          <>
            {effect && <p className="text-[12px] text-[#C0C0C0] mt-3">{effect}</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setAction(null)} className="flex-1 text-sm font-semibold text-[#A0A0A0] border border-white/10 rounded-lg py-2.5 min-h-[44px]">Back</button>
              <button disabled={busy} onClick={run}
                className={`flex-1 text-sm font-bold text-white rounded-lg py-2.5 min-h-[44px] disabled:opacity-50 ${action === "cancel" ? "bg-amber-600 hover:bg-amber-700" : action === "free" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#9D00FF] hover:bg-[#8C00E6]"}`}>
                {busy ? "…" : action === "qty" ? "Save quantity" : action === "cancel" ? "Cancel item" : action === "free" ? "Make it free" : "Charge again"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
